class OktaSessionMonitor {
  constructor(oktaAuth, onSessionExpired) {
    this.oktaAuth = oktaAuth;
    this.onSessionExpired = onSessionExpired;
    this.isMonitoring = false;
    this.authStateSubscription = null;
    this.tokenManagerSubscription = null;
    this.sessionCheckInterval = null;

    this.handleAuthStateChange = this.handleAuthStateChange.bind(this);
    this.handleTokenEvent = this.handleTokenEvent.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.checkOktaSession = this.checkOktaSession.bind(this);
  }

  start() {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;

    if (this.oktaAuth.authStateManager) {
      this.authStateSubscription = this.oktaAuth.authStateManager.subscribe(
        this.handleAuthStateChange
      );
    }

    if (this.oktaAuth.tokenManager) {
      this.oktaAuth.tokenManager.on('expired', this.handleTokenEvent);
      this.oktaAuth.tokenManager.on('error', this.handleTokenEvent);
      this.oktaAuth.tokenManager.on('removed', this.handleTokenEvent);
      this.tokenManagerSubscription = true;
    }

    window.addEventListener('storage', this.handleStorageChange);

    // Add periodic session check as fallback (every 60 seconds)
    this.sessionCheckInterval = setInterval(this.checkOktaSession, 60000);
  }

  stop() {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.authStateSubscription) {
      this.authStateSubscription();
      this.authStateSubscription = null;
    }

    if (this.oktaAuth.tokenManager && this.tokenManagerSubscription) {
      this.oktaAuth.tokenManager.off('expired', this.handleTokenEvent);
      this.oktaAuth.tokenManager.off('error', this.handleTokenEvent);
      this.oktaAuth.tokenManager.off('removed', this.handleTokenEvent);
      this.tokenManagerSubscription = null;
    }

    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    window.removeEventListener('storage', this.handleStorageChange);
  }

  handleAuthStateChange(authState) {
    if (!authState.isAuthenticated) {
      this.triggerLogout();
      return;
    }

    if (!authState.accessToken) {
      this.triggerLogout();
    }
  }

  handleTokenEvent(key, token, error) {
    if (key === 'accessToken') {
      if (error || !token) {
        this.triggerLogout();
      }
    }
  }

  handleStorageChange(event) {
    if (event.key === 'okta-logout-event' && event.newValue) {
      this.triggerLogout();
    }
  }

  async verifyCurrentAuthState() {
    try {
      if (!this.oktaAuth.authStateManager) {
        return;
      }

      const authState = this.oktaAuth.authStateManager.getAuthState();

      if (!authState.isAuthenticated || !authState.accessToken) {
        this.triggerLogout();
      }
    } catch (error) {
      this.triggerLogout();
    }
  }

  async checkOktaSession() {
    if (!this.isMonitoring) {
      return;
    }

    try {
      const accessToken = await this.oktaAuth.tokenManager.get('accessToken');

      if (!accessToken) {
        this.triggerLogout();
        return;
      }

      const now = Date.now() / 1000;
      if (accessToken?.expiresAt && accessToken.expiresAt <= now) {
        this.triggerLogout();
        return;
      }
    } catch (error) {
      console.warn('Session check failed:', error.message);
    }
  }

  triggerLogout() {
    if (!this.isMonitoring) {
      return;
    }

    try {
      localStorage.setItem('okta-logout-event', Date.now().toString());
      setTimeout(() => {
        localStorage.removeItem('okta-logout-event');
      }, 1000);
    } catch (error) {
      console.warn('Failed to broadcast logout event:', error);
    }

    this.stop();

    if (this.onSessionExpired) {
      this.onSessionExpired();
    }
  }

  async checkCurrentSession() {
    if (!this.oktaAuth.authStateManager) {
      return false;
    }

    const authState = this.oktaAuth.authStateManager.getAuthState();

    return authState.isAuthenticated && !!authState.accessToken;
  }
}

export default OktaSessionMonitor;
